<script setup>
/**
 * ProductTabs — four product modules surfaced via tabs.
 *
 * v3 changes:
 *  - HTML mockups replaced with real screenshots of the running app
 *    inside a browser-frame component (LandingScreenshotFrame).
 *  - Each tab carries an array of screenshots; within a tab the
 *    images auto-cycle, and the tab itself auto-rotates with a dwell
 *    proportional to the image count so every variant is shown at
 *    least once before the tab advances.
 *  - "Reportes" renamed to "Informes" to match the actual product nav.
 */
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import { Activity, BarChart3, Bell, Clock, Check, ChevronDown } from "lucide-vue-next";
import AnimatedSection from "@/components/AnimatedSection.vue";
import LandingScreenshotFrame from "./LandingScreenshotFrame.vue";

// Real product screenshots, one set per supported locale.
// File convention: <slug>_<locale>.png (locale ∈ es|en|el).
// `import.meta.glob` resolves all matching files at build time, so the
// bundler still tree-shakes and fingerprints them like normal imports.
const screenshotModules = import.meta.glob(
  "@/assets/landing/screens/*.png",
  { eager: true, import: "default" }
);

// Resolve a screenshot URL for a given slug and locale, falling back to
// Spanish when the localised file is missing (during the transition
// period where we reuse the ES captures for EN/EL).
const SUPPORTED_LOCALES = ["es", "en", "el"];
const screenshotUrl = (slug, locale) => {
  const lc = SUPPORTED_LOCALES.includes(locale) ? locale : "es";
  const key = `/src/assets/landing/screens/${slug}_${lc}.png`;
  return (
    screenshotModules[key] ||
    screenshotModules[`/src/assets/landing/screens/${slug}_es.png`]
  );
};

const { t, locale } = useI18n();

const IMAGE_ROTATE_MS = 4200;

const tabs = computed(() => [
  {
    id: "realtime",
    icon: Activity,
    label: t("landing.product_tabs.tabs.realtime.label"),
    title: t("landing.product_tabs.tabs.realtime.title"),
    description: t("landing.product_tabs.tabs.realtime.description"),
    bullets: [
      t("landing.product_tabs.tabs.realtime.bullets.0"),
      t("landing.product_tabs.tabs.realtime.bullets.1"),
      t("landing.product_tabs.tabs.realtime.bullets.2"),
    ],
    images: [
      { src: screenshotUrl("realtime-1", locale.value), alt: t("landing.product_tabs.tabs.realtime.label"), url: "app.fenixguardian.com/realtime" },
      { src: screenshotUrl("realtime-2", locale.value), alt: t("landing.product_tabs.tabs.realtime.label"), url: "app.fenixguardian.com/realtime" },
    ],
  },
  {
    id: "timeline",
    icon: Clock,
    label: t("landing.product_tabs.tabs.timeline.label"),
    title: t("landing.product_tabs.tabs.timeline.title"),
    description: t("landing.product_tabs.tabs.timeline.description"),
    bullets: [
      t("landing.product_tabs.tabs.timeline.bullets.0"),
      t("landing.product_tabs.tabs.timeline.bullets.1"),
      t("landing.product_tabs.tabs.timeline.bullets.2"),
    ],
    images: [
      { src: screenshotUrl("timeline-1", locale.value), alt: t("landing.product_tabs.tabs.timeline.label"), url: "app.fenixguardian.com/activities/timeline" },
      { src: screenshotUrl("timeline-2", locale.value), alt: t("landing.product_tabs.tabs.timeline.label"), url: "app.fenixguardian.com/app/employee/avery-thomas" },
    ],
  },
  {
    id: "reports",
    icon: BarChart3,
    label: t("landing.product_tabs.tabs.reports.label"),
    title: t("landing.product_tabs.tabs.reports.title"),
    description: t("landing.product_tabs.tabs.reports.description"),
    bullets: [
      t("landing.product_tabs.tabs.reports.bullets.0"),
      t("landing.product_tabs.tabs.reports.bullets.1"),
      t("landing.product_tabs.tabs.reports.bullets.2"),
    ],
    images: [
      { src: screenshotUrl("reports-1", locale.value), alt: t("landing.product_tabs.tabs.reports.label"), url: "app.fenixguardian.com/trends" },
      { src: screenshotUrl("reports-2", locale.value), alt: t("landing.product_tabs.tabs.reports.label"), url: "app.fenixguardian.com/reports/apps-websites" },
      { src: screenshotUrl("reports-3", locale.value), alt: t("landing.product_tabs.tabs.reports.label"), url: "app.fenixguardian.com/reports/location-insights" },
      { src: screenshotUrl("reports-4", locale.value), alt: t("landing.product_tabs.tabs.reports.label"), url: "app.fenixguardian.com/reports/work-types" },
    ],
  },
  {
    id: "alerts",
    icon: Bell,
    label: t("landing.product_tabs.tabs.alerts.label"),
    title: t("landing.product_tabs.tabs.alerts.title"),
    description: t("landing.product_tabs.tabs.alerts.description"),
    bullets: [
      t("landing.product_tabs.tabs.alerts.bullets.0"),
      t("landing.product_tabs.tabs.alerts.bullets.1"),
      t("landing.product_tabs.tabs.alerts.bullets.2"),
    ],
    images: [
      { src: screenshotUrl("alerts-1", locale.value), alt: t("landing.product_tabs.tabs.alerts.label"), url: "app.fenixguardian.com/alerts" },
      { src: screenshotUrl("alerts-2", locale.value), alt: t("landing.product_tabs.tabs.alerts.label"), url: "app.fenixguardian.com/alerts" },
    ],
  },
]);

const active = ref(tabs.value[0].id);
const activeTab = computed(() => tabs.value.find((t) => t.id === active.value));
const userInteracted = ref(false);

// Tab auto-rotate: dwell scales with image count so each variant has a
// chance to appear before the tab advances.
let rotateTimer;
const activeDwellMs = computed(() => {
  const n = activeTab.value?.images?.length ?? 1;
  return Math.max(7000, n * IMAGE_ROTATE_MS + 800);
});
const scheduleNext = () => {
  if (rotateTimer) clearTimeout(rotateTimer);
  if (userInteracted.value) return;
  rotateTimer = setTimeout(() => {
    if (userInteracted.value) return;
    const i = tabs.value.findIndex((t) => t.id === active.value);
    active.value = tabs.value[(i + 1) % tabs.value.length].id;
    scheduleNext();
  }, activeDwellMs.value);
};
const selectTab = (id) => {
  userInteracted.value = true;
  if (rotateTimer) clearTimeout(rotateTimer);
  active.value = id;
};
onMounted(scheduleNext);
onUnmounted(() => {
  if (rotateTimer) clearTimeout(rotateTimer);
});

// Mobile accordion: which tab is expanded
const mobileExpanded = ref(tabs.value[0].id);
const toggleMobile = (id) => {
  mobileExpanded.value = mobileExpanded.value === id ? null : id;
};
</script>

<template>
  <section id="features" class="py-24 bg-background scroll-mt-20">
    <div class="container mx-auto px-6">
      <AnimatedSection animation="fade-up" class="text-center mb-12 max-w-3xl mx-auto">
        <p class="text-xs font-semibold uppercase tracking-widest text-accent mb-3">
          {{ t("landing.product_tabs.eyebrow") }}
        </p>
        <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4 tracking-tight">
          {{ t("landing.product_tabs.title") }}
        </h2>
        <p class="text-lg text-muted-foreground">
          {{ t("landing.product_tabs.subtitle") }}
        </p>
      </AnimatedSection>

      <!-- Desktop: tab strip + auto-rotating content -->
      <div class="hidden md:block">
        <AnimatedSection animation="fade-up" :delay="100">
          <div class="flex justify-center mb-10">
            <div class="inline-flex gap-1 p-1.5 rounded-xl border border-border bg-card overflow-x-auto max-w-full">
              <button
                v-for="tab in tabs"
                :key="tab.id"
                type="button"
                :class="[
                  'inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all relative overflow-hidden',
                  active === tab.id
                    ? 'bg-accent text-accent-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                ]"
                @click="selectTab(tab.id)"
              >
                <component :is="tab.icon" class="w-4 h-4 relative z-10" />
                <span class="relative z-10">{{ tab.label }}</span>
              </button>
            </div>
          </div>
        </AnimatedSection>

        <!-- Crossfade between tab contents using <Transition mode="out-in"> -->
        <Transition name="tab-fade" mode="out-in">
          <div :key="activeTab.id" class="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            <div class="lg:col-span-5 order-2 lg:order-1">
              <h3 class="text-2xl md:text-3xl font-bold text-foreground mb-4 tracking-tight">
                {{ activeTab.title }}
              </h3>
              <p class="text-base text-muted-foreground mb-6 leading-relaxed">
                {{ activeTab.description }}
              </p>
              <ul class="space-y-3">
                <li
                  v-for="(bullet, i) in activeTab.bullets"
                  :key="i"
                  class="flex items-start gap-3 text-sm text-foreground"
                >
                  <span class="flex-shrink-0 w-5 h-5 rounded-full bg-accent/15 flex items-center justify-center mt-0.5">
                    <Check class="w-3 h-3 text-accent" />
                  </span>
                  <span>{{ bullet }}</span>
                </li>
              </ul>
            </div>

            <div class="lg:col-span-7 order-1 lg:order-2">
              <LandingScreenshotFrame :images="activeTab.images" :rotate-ms="IMAGE_ROTATE_MS" />
            </div>
          </div>
        </Transition>
      </div>

      <!-- Mobile: stacked accordion of all 4 modules -->
      <div class="md:hidden space-y-3">
        <div
          v-for="tab in tabs"
          :key="tab.id"
          class="rounded-xl border border-border bg-card overflow-hidden"
        >
          <button
            type="button"
            class="w-full flex items-center gap-3 px-4 py-4 text-left"
            @click="toggleMobile(tab.id)"
          >
            <div
              :class="[
                'w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0',
                mobileExpanded === tab.id ? 'bg-accent text-accent-foreground' : 'bg-muted text-muted-foreground'
              ]"
            >
              <component :is="tab.icon" class="w-4 h-4" />
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-bold text-foreground">{{ tab.label }}</p>
              <p class="text-xs text-muted-foreground truncate">
                {{ tab.title }}
              </p>
            </div>
            <ChevronDown
              :class="[
                'w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform',
                mobileExpanded === tab.id ? 'rotate-180' : ''
              ]"
            />
          </button>

          <Transition name="accordion">
            <div v-if="mobileExpanded === tab.id" class="px-4 pb-5">
              <p class="text-sm text-muted-foreground mb-4 leading-relaxed">
                {{ tab.description }}
              </p>
              <ul class="space-y-2 mb-5">
                <li
                  v-for="(bullet, i) in tab.bullets"
                  :key="i"
                  class="flex items-start gap-2 text-xs text-foreground"
                >
                  <Check class="w-3.5 h-3.5 mt-0.5 text-accent flex-shrink-0" />
                  <span>{{ bullet }}</span>
                </li>
              </ul>
              <LandingScreenshotFrame :images="tab.images" :rotate-ms="IMAGE_ROTATE_MS" />
            </div>
          </Transition>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* Tab content crossfade — softer than instant swap */
.tab-fade-enter-active,
.tab-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.tab-fade-enter-from {
  opacity: 0;
  transform: translateY(8px);
}
.tab-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* Accordion expand on mobile */
.accordion-enter-active,
.accordion-leave-active {
  transition: opacity 0.25s ease, max-height 0.3s ease;
  overflow: hidden;
}
.accordion-enter-from,
.accordion-leave-to {
  opacity: 0;
  max-height: 0;
}
.accordion-enter-to,
.accordion-leave-from {
  opacity: 1;
  max-height: 800px;
}
</style>
